import * as React from 'react';
import CloseIcon from '@material-ui/icons/Close';
import {
  applyFormStateFromStorage,
  onAfterRenderQuestion,
  onCurrentPageChanged,
  onCurrentPageChanging,
  onLoadChoicesFromServer,
  onSurveyComplete,
  onUpdateQuestionCssClasses,
  onValueChanged,
  onVariableChanged
} from '../helpers/registerSurveyCallbacks';
import { Box, Dialog, IconButton } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Error } from '@pec/aion-ui-components/components/Error';
import { getShowDetails } from '../helpers/storage';
import { InfoDialog } from './InfoDialog';
import { ITaxRate } from '../types/products';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { LoadingOverlay } from '@pec/aion-ui-components/components/LoadingOverlay';
import { MainLayout } from '../containers/MainLayout';
import { MemoSurvey } from './MemoSurvey';
import { Model, Question, settings } from 'survey-core';
import { Navigation } from './Navigation';
import { OperationType } from '../types/companyDetails';
import { registerCustomComponents } from './questionTypes/registerCustomComponents';
import { registerCustomFunctions } from '../helpers/customFunctions';
import { registerCustomProperties } from '../helpers/customProperties';
import { schema as schemaRegularSignup } from '../schema';
import { schema as schemaFoundationSignup } from '../schemaFoundation';
import { StepsContainer } from '../containers/StepsContainer';
import { useAppStateContext } from '../contexts/AppState';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isFeatureEnabled } from '@pec/aion-ui-core/helpers/featureFlags';

registerCustomProperties();
registerCustomFunctions();
registerCustomComponents();

// For subscription field choicesByUrl has optional parameters that are build with expression
settings.webserviceEncodeParameters = false;

const useStyles = makeStyles(theme =>
  createStyles({
    formContainer: {
      maxWidth: '1040px',
      margin: `${theme.spacing(3)}px auto 0`
    },
    dialogClose: {
      position: 'absolute',
      top: 8,
      right: 8
    }
  })
);

export const Form: React.FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const [formState, setFormState] = React.useState<{ isLoading: boolean; error?: string }>({ isLoading: false });
  const [survey, setSurvey] = React.useState<Model | null>(null);
  const [firstInputQuestion, setFirstInputQuestion] = React.useState<Question | null>(null);
  const [focusFirstControl, setFocusFirstControl] = React.useState<boolean>(true);
  const [showUserExistsDialog, setShowUserExistsDialog] = React.useState<boolean>(false);
  const [recurringBillingEnabled, setRecurringBillingEnabled] = React.useState<boolean>(false);
  const [bankTransferEnabled, setBankTransferEnabled] = React.useState<boolean>(false);
  const formDataChangedRef = React.useRef(false);
  const taxRatesCacheRef = React.useRef<Record<string, ITaxRate | null>>({});
  const { updateFormData, setScrollPosition } = useAppStateContext();
  const classes = useStyles();
  const isAdmin = new URLSearchParams(search).get('admin') === 'true';
  const isFoundation = new URLSearchParams(search).get('foundation') === 'true';
  const country = new URLSearchParams(search).get('country') || '';
  const crmCorrelationId = new URLSearchParams(search).get('corrId') || undefined;

  const signUpConfig = isFoundation
    ? { schema: schemaFoundationSignup, operationType: OperationType.FOUNDATION }
    : { schema: schemaRegularSignup, operationType: OperationType.SIGNUP };

  React.useEffect(() => {
    const survey = new Model(signUpConfig.schema(t, Boolean(recurringBillingEnabled), Boolean(bankTransferEnabled)));
    survey.setVariable('showDetails', getShowDetails());
    survey.setVariable('isAdmin', isAdmin);
    survey.setVariable('signupType', signUpConfig.operationType);
    survey.setVariable('crmCorrelationId', crmCorrelationId);
    survey.onUpdateQuestionCssClasses.add(onUpdateQuestionCssClasses);
    applyFormStateFromStorage(survey, updateFormData);
    survey.onLoadChoicesFromServer.add(onLoadChoicesFromServer(taxRatesCacheRef));
    survey.onCompleting.add(onSurveyComplete(setFormState, t));
    survey.onCurrentPageChanging.add(onCurrentPageChanging(formDataChangedRef));
    survey.onCurrentPageChanged.add(onCurrentPageChanged(setScrollPosition, setFocusFirstControl));
    survey.onValueChanged.add(onValueChanged(formDataChangedRef, updateFormData));
    survey.onVariableChanged.add(onVariableChanged(setShowUserExistsDialog));
    survey.onAfterRenderQuestion.add(onAfterRenderQuestion);
    setSurvey(survey);
  }, [recurringBillingEnabled, bankTransferEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    isFeatureEnabled('RecurringBillingEnabled').then(isEnabled => setRecurringBillingEnabled(isEnabled));
    isFeatureEnabled('BankTransferEnabled').then(isEnabled => setBankTransferEnabled(isEnabled));
  }, []);

  React.useEffect(() => {
    if (survey) {
      const firstInputOnPage = survey.currentPage.questions.find(
        (question: Question) => question.hasInput && question.isVisible
      );
      setFirstInputQuestion(firstInputOnPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey, setFirstInputQuestion, survey?.currentPage]);

  if (!survey) return <Loading />;

  const onDialogClose = () => {
    setFormState({ isLoading: false });
  };

  const handleConfirm = () => {
    setShowUserExistsDialog(false);
  };

  return (
    <MainLayout
      firstInputQuestion={firstInputQuestion}
      focusFirstControl={focusFirstControl}
      setFocusFirstControl={setFocusFirstControl}
    >
      <Box className={classes.formContainer}>
        <LoadingOverlay isLoading={formState.isLoading}>
          <StepsContainer survey={survey} />
          <MemoSurvey model={survey} />
          <Navigation survey={survey} />
        </LoadingOverlay>
        <Dialog open={!!formState.error} onClose={onDialogClose}>
          <IconButton className={classes.dialogClose} size="small" onClick={onDialogClose}>
            <CloseIcon />
          </IconButton>
          <Error message={formState.error} />
        </Dialog>
        <InfoDialog
          open={showUserExistsDialog}
          onClose={() => setShowUserExistsDialog(false)}
          onConfirm={handleConfirm}
          country={country}
        />
      </Box>
    </MainLayout>
  );
};
